import { num } from '../../scene/component-types.js';
import type { SettingsSection } from '../settings-types.js';

/**
 * `settings.ui` (prism_core::ui::UiProjectSettings); see
 * engine/docs/systems/ui.md. Defaults mirror the engine.
 */
const ui: SettingsSection = {
  key: 'ui',
  label: 'UI',
  icon: 'ui',
  order: 50,
  hint: 'Default font, style sheets and documents shown at start',
  fields: [
    {
      key: 'defaultFont',
      label: 'Default font',
      type: { kind: 'path', suffix: ['.ttf', '.otf'] },
      hint: 'TTF / OTF used where no style sets a font; empty = the embedded font',
    },
    {
      key: 'styles',
      label: 'Style sheets',
      type: { kind: 'json' },
      hint: 'array of *.uistyle.json applied to every document (after the built-in neutral sheet), e.g. ["ui/theme.uistyle.json"]',
    },
    {
      key: 'startDocuments',
      label: 'Start documents',
      type: { kind: 'json' },
      hint: 'array of *.ui.json shown when play starts and after each scene load, e.g. ["ui/hud.ui.json"]',
    },
    {
      key: 'screenDpi',
      label: 'Screen DPI',
      type: num(1, { min: 1 }),
      hint: 'for constantPhysicalSize scalers; empty = the scaler’s fallback DPI',
    },
    {
      key: 'dragThreshold',
      label: 'Drag threshold',
      type: num(1, { min: 0 }),
      hint: 'pixels the pointer moves before a press on a draggable element starts a drag; empty = 6',
    },
    {
      key: 'doubleClickTime',
      label: 'Double click time',
      type: num(0.05, { min: 0 }),
      hint: 'seconds between the two clicks of a double click; empty = 0.3',
    },
  ],
};

const scaler: SettingsSection = {
  key: 'ui',
  label: 'UI canvas scaler',
  icon: 'ui',
  order: 51,
  hint: 'Used by every screen-space document that has no scaler of its own',
  fields: [
    {
      key: 'scaler',
      label: 'Scaler',
      type: {
        kind: 'object',
        fields: [
          {
            key: 'mode',
            label: 'Mode',
            type: { kind: 'enum', options: ['constantPixelSize', 'scaleWithScreenSize', 'constantPhysicalSize'] },
            read: () => 'constantPixelSize',
          },
          {
            key: 'scaleFactor',
            label: 'Scale factor',
            type: num(0.1, { min: 0.01 }),
            read: () => 1,
            hint: 'screen pixels per canvas unit',
            showIf: (v) => (v.mode ?? 'constantPixelSize') === 'constantPixelSize',
          },
          {
            key: 'referenceResolution',
            label: 'Reference resolution',
            type: { kind: 'json' },
            read: () => [1920, 1080],
            hint: '[width, height] the layouts were designed for',
            showIf: (v) => v.mode === 'scaleWithScreenSize',
          },
          {
            key: 'screenMatchMode',
            label: 'Screen match',
            type: { kind: 'enum', options: ['matchWidthOrHeight', 'expand', 'shrink'] },
            read: () => 'matchWidthOrHeight',
            showIf: (v) => v.mode === 'scaleWithScreenSize',
          },
          {
            key: 'match',
            label: 'Match (width ↔ height)',
            type: num(0.1, { min: 0, max: 1 }),
            read: () => 0,
            showIf: (v) => v.mode === 'scaleWithScreenSize' && (v.screenMatchMode ?? 'matchWidthOrHeight') === 'matchWidthOrHeight',
          },
          {
            key: 'physicalUnit',
            label: 'Physical unit',
            type: { kind: 'enum', options: ['centimeters', 'millimeters', 'inches', 'points', 'picas'] },
            read: () => 'points',
            showIf: (v) => v.mode === 'constantPhysicalSize',
          },
          {
            key: 'fallbackScreenDpi',
            label: 'Fallback DPI',
            type: num(1, { min: 1 }),
            read: () => 96,
            showIf: (v) => v.mode === 'constantPhysicalSize',
          },
        ],
      },
    },
  ],
};

const navigation: SettingsSection = {
  key: 'ui',
  label: 'UI navigation',
  icon: 'ui',
  order: 52,
  hint: 'Keyboard / gamepad focus. Space and Enter never press a focused control; bind a submit action for that',
  fields: [
    {
      key: 'navigation',
      label: 'Navigation',
      type: {
        kind: 'object',
        fields: [
          { key: 'enabled', label: 'Enabled', type: { kind: 'bool' }, read: () => true },
          {
            key: 'directionKeys',
            label: 'Arrow keys / d-pad',
            type: { kind: 'bool' },
            read: () => true,
            hint: 'arrows and the gamepad d-pad move focus once something is focused; Tab / Shift+Tab cycle',
          },
          {
            key: 'submitAction',
            label: 'Submit action',
            type: { kind: 'string', placeholder: 'none' },
            hint: 'input action (Input settings) that presses the focused button / toggle / dropdown; empty = none',
          },
          {
            key: 'cancelAction',
            label: 'Cancel action',
            type: { kind: 'string', placeholder: 'none' },
            hint: 'input action that sends the cancel event; empty = none',
          },
          {
            key: 'navigateAction',
            label: 'Navigate actions',
            type: {
              kind: 'object',
              fields: [
                { key: 'up', label: 'Up', type: { kind: 'string', placeholder: 'none' } },
                { key: 'down', label: 'Down', type: { kind: 'string', placeholder: 'none' } },
                { key: 'left', label: 'Left', type: { kind: 'string', placeholder: 'none' } },
                { key: 'right', label: 'Right', type: { kind: 'string', placeholder: 'none' } },
              ],
            },
            hint: 'extra input actions that move focus (e.g. bound to Gamepad_LeftY+)',
          },
          {
            key: 'autoFocus',
            label: 'Auto focus',
            type: { kind: 'bool' },
            read: () => false,
            hint: 'focus the first control when a document appears',
          },
        ],
      },
    },
  ],
};

export default [ui, scaler, navigation];
